'use client';

import CustomTooltip from '@/components/custom-tooltip';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuPortal,
    DropdownMenuSeparator,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { SidebarItemProps } from '@/hooks/use-sidebar-store';
import { AllSubKeysMessageResource } from '@/i18n/messages';
import { cn } from '@/lib/utils';
import { Link } from '@/navigation';
import { DropdownMenuArrow } from '@radix-ui/react-dropdown-menu';
import { Ellipsis } from 'lucide-react';
import { useTranslations } from 'next-intl';
import {
    Dispatch,
    MouseEventHandler,
    PropsWithChildren,
    SetStateAction,
    useCallback,
    useState,
} from 'react';
import { CustomLayoutIcon } from './custom-layout-icon';
import { CustomSidebarCounter } from './custom-sidebar-counter';

type CustomMenuContentProps = {
    className?: string;
    pathname: string;
    menus?: SidebarItemProps[];
    onClickMenuItem?: (item: SidebarItemProps) => void;
};
type CustomIconOrEllipsisProps = {
    className?: string;
    menu: SidebarItemProps;
};
type CustomMenuIconContentProps = {
    className?: string;
    pathname: string;
    item: SidebarItemProps;
    isDesktop?: boolean;
};

const defIconClassName = 'min-h-5 min-w-5';

const CustomMenuContent = ({
    className,
    menus,
    pathname,
    onClickMenuItem,
}: CustomMenuContentProps) => {
    const transSidebar = useTranslations('Sidebar');

    return menus?.map((x, idx) => {
        const isCurrent = x.href === pathname;
        const itemBackground = isCurrent
            ? 'bg-primary text-primary-foreground '
            : 'hover:bg-primary/30';

        const itemName = x.appLanguageResourceCode
            ? transSidebar(
                  x.appLanguageResourceCode as AllSubKeysMessageResource<'Sidebar'>
              ) //TODO: type eror
            : x.name;

        const content = (
            <>
                {x.icon && (
                    <CustomLayoutIcon
                        component={x.icon.component}
                        className={cn(defIconClassName, x.icon.className, {
                            'text-primary-foreground': isCurrent,
                        })}
                    />
                )}
                <p className='w-full truncate'>{itemName}</p>
            </>
        );

        if (x.children && x.children.length > 0) {
            return (
                <DropdownMenuSub key={idx}>
                    <DropdownMenuSubTrigger
                        className={cn(
                            'mb-1 flex cursor-pointer items-center gap-2 py-1.5',
                            className,
                            itemBackground
                        )}
                    >
                        {content}
                    </DropdownMenuSubTrigger>

                    <DropdownMenuPortal>
                        <DropdownMenuSubContent className='h-[95vh] min-w-52 overflow-y-auto'>
                            <DropdownMenuLabel className='truncate'>
                                {x.name}
                            </DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <CustomMenuContent
                                menus={x.children}
                                pathname={pathname}
                                onClickMenuItem={onClickMenuItem}
                            />
                        </DropdownMenuSubContent>
                    </DropdownMenuPortal>
                </DropdownMenuSub>
            );
        }

        return (
            <DropdownMenuItem asChild key={idx}>
                <CustomMenuLink
                    href={x.href}
                    className={cn(
                        'mb-1 flex cursor-pointer items-center gap-2 py-2',
                        itemBackground,
                        className
                    )}
                    onClick={() => onClickMenuItem?.(x)}
                >
                    {content}

                    <CustomSidebarCounter
                        item={x}
                        className={cn('', {
                            'bg-secondary/80 text-secondary-foreground':
                                isCurrent,
                            'bg-primary/80 text-primary-foreground': !isCurrent,
                        })}
                    />
                </CustomMenuLink>
            </DropdownMenuItem>
        );
    });
};

export const CustomMenuIconContent = ({
    className,
    item,
    pathname,
    isDesktop,
}: CustomMenuIconContentProps) => {
    const [isOpen, setIsOpen] = useState(false);

    const transSidebar = useTranslations('Sidebar');

    const onClickMenuItem = useCallback((item: SidebarItemProps) => {
        if (item && item.href) {
            setIsOpen(false);
        }
    }, []);

    //TODO: move
    const nodeLevel = item.key?.split('_')?.length ?? 1;
    const hasChildren = item.children && item.children.length > 0;

    const content = item.appLanguageResourceCode
        ? transSidebar(
              item.appLanguageResourceCode as AllSubKeysMessageResource<'Sidebar'>
          ) //TODO: type eror
        : item.name;

    return (
        <div
            className={cn(
                'bg-sidebar text-sidebar-foreground',
                {
                    'max-w-full': nodeLevel > 1,
                },
                className
            )}
        >
            <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
                <CustomTooltip.Root
                    tooltipTriggerProps={{ asChild: true }}
                    tooltipContentProps={{ side: 'right', align: 'center' }}
                    title={content}
                >
                    <DropdownMenuTrigger asChild>
                        <Button
                            variant={'secondary'}
                            className={cn(
                                'delay-50 mb-2 flex w-full justify-center rounded bg-sidebar px-3 text-sidebar-foreground',
                                item.href === pathname
                                    ? 'bg-primary text-primary-foreground hover:text-primary'
                                    : 'hover:bg-primary/30'
                            )}
                        >
                            <CustomMenuLink
                                href={item.href}
                                className={cn(
                                    'flex items-center gap-4 rounded-lg',
                                    item.className
                                )}
                            >
                                <CustomIconOrEllipsis menu={item} />
                            </CustomMenuLink>
                        </Button>
                    </DropdownMenuTrigger>
                </CustomTooltip.Root>

                {/* render child */}
                {hasChildren && (
                    <DropdownMenuContent
                        side='right'
                        align='start'
                        className='min-w-80 overflow-y-auto bg-background text-foreground'
                    >
                        <DropdownMenuLabel className='truncate'>
                            {content}
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <CustomMenuContent
                            menus={item.children}
                            pathname={pathname}
                            onClickMenuItem={onClickMenuItem}
                        />
                        <DropdownMenuArrow className='fill-border' />
                    </DropdownMenuContent>
                )}
            </DropdownMenu>
        </div>
    );
};

const CustomIconOrEllipsis = ({
    menu: item,
    className,
}: CustomIconOrEllipsisProps) => {
    return item.icon ? (
        <CustomLayoutIcon
            {...item.icon}
            className={cn(defIconClassName, item.icon.className, className)}
        />
    ) : (
        <CustomLayoutIcon
            component={Ellipsis}
            className={cn(defIconClassName, className)}
        />
    );
};
export const CustomMenuLink = ({
    children,
    onClick,
    ...rest
}: PropsWithChildren<{
    className?: string;
    href?: string;
    onClick?: MouseEventHandler<HTMLDivElement | HTMLAnchorElement>;
}>) => {
    if (rest.href && rest.href != '') {
        return (
            <Link onClick={onClick} href={rest.href}>
                <div className={rest.className}>{children}</div>
            </Link>
        );
    }

    return (
        <div className={rest.className} onClick={onClick}>
            {children}
        </div>
    );
};
export const CustomMenuOverlay = ({
    children,
    className,
    open,
    setOpen,
}: PropsWithChildren<{
    open: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>;
    className?: string;
}>) => {
    return (
        <Sheet open={open} onOpenChange={setOpen}>
            {/* <SheetTrigger asChild>
                <Button
                    variant='secondary'
                    size={'icon'}
                    className='absolute z-20 h-9 my-1 shrink-0 rounded-r-full md:hidden'
                >
                    <ChevronRight strokeWidth='3' className={cn('h-5 w-5')} />
                    <span className='sr-only'>Toggle sidebar icon</span>
                </Button>
            </SheetTrigger> */}
            <SheetContent
                side='left'
                className={cn(
                    'w-3/4 overflow-y-auto bg-sidebar p-0 pt-10 text-sm',
                    className
                )}
                autoFocus={false}
                onOpenAutoFocus={(el) => {
                    el.preventDefault();
                }}
            >
                {children}
            </SheetContent>
        </Sheet>
    );
};
